// Shared source-citation chip (SOURCE_CITATION_AND_PREFS_PLAN.md, Task 4).
// One visual language for "where did this come from": trusted external sources
// and our curated tables read as neutral/confident; AI estimates get a distinct
// colour so the user can weigh them differently. When the source has a stable
// per-drug page, the chip is an outbound link (↗).

import { DATA_SOURCE_META, dataSourceUrl, type DataSourceKind } from "@medassist/shared";

const TRUST_CLS: Record<string, string> = {
  trusted: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  ai: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  user: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
};

export function SourceBadge({
  source,
  query,
  prefix = "Source",
  className = "",
}: {
  source: DataSourceKind;
  /** Drug/query name used to build the outbound link, when the source has one. */
  query?: string;
  /** Leading text before the label, e.g. "Source". Pass null to omit. */
  prefix?: string | null;
  className?: string;
}) {
  const meta = DATA_SOURCE_META[source];
  const url = dataSourceUrl(source, query);
  const text = `${prefix ? `${prefix}: ` : ""}${meta.label}`;
  const base = `inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium ${TRUST_CLS[meta.trust]} ${className}`;

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`${base} hover:underline`}
        title={query ? `Open ${meta.label} for ${query}` : `Open ${meta.label}`}
      >
        {text} <span aria-hidden="true">↗</span>
      </a>
    );
  }
  return <span className={base}>{text}</span>;
}

// Canonical display order (rough trust order), so the legend reads consistently
// regardless of the order sources happen to appear in the data.
const KIND_ORDER = Object.keys(DATA_SOURCE_META) as DataSourceKind[];

/**
 * A small "what the sources mean" key: one row per distinct source present, each
 * pairing the badge with its plain-English description (DATA_SOURCE_META.description).
 * Pass the sources that actually appear on screen so users only see relevant rows.
 */
export function SourceLegend({
  sources,
  className = "",
}: {
  sources: (DataSourceKind | undefined)[];
  className?: string;
}) {
  const present = KIND_ORDER.filter((k) => sources.includes(k));
  if (present.length === 0) return null;

  return (
    <div className={className}>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        What the sources mean
      </p>
      {/* Grid with a shared max-content first column so every badge sits in one
          column and all descriptions line up at the same left edge, regardless of
          differing badge widths. `li.contents` keeps the markup semantic while
          letting the badge + description become direct grid items. */}
      <ul className="grid grid-cols-[max-content_1fr] items-start gap-x-2 gap-y-1.5">
        {present.map((s) => (
          <li key={s} className="contents">
            <SourceBadge source={s} prefix={null} />
            <span className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              {DATA_SOURCE_META[s].description}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
